import React, { use, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Toolbar,
  Typography,
  Link as MuiLink,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import img from "../images/Exhibitionandevents2.webp";
import { Link, useNavigate } from "react-router";
import { ENV } from "@app/constants/env";
import { useLogin } from "@sdk-react-identity/core/hooks";

const SERVICES = [
  [
    "Event Management",
    "Complete event planning and execution services for trade shows and exhibitions",
  ],
  [
    "Logistics Solutions",
    "Efficient transportation and logistics management for your events",
  ],
  [
    "Fair Organization",
    "Professional fair and exhibition organization services",
  ],
  [
    "Quality Assurance",
    "Ensuring the highest standards in all our service deliveries",
  ],
  [
    "Cost Optimization",
    "Maximizing value while minimizing costs for your events",
  ],
  [
    "24/7 Support",
    "Round-the-clock customer support for all your needs",
  ],
] as const;

const FOOTER_SECTIONS = [
  [
    "Services",
    ["Event Management", "Logistics", "Fair Organization"],
  ],
  ["Company", ["About Us", "Careers", "Contact"]],
  ["Support", ["Help Center", "Documentation", "Privacy Policy"]],
] as const;

export const CommonLayout = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
 
  const handleTrackshipments = () => {
    navigate("/trackshipments");
    setMobileMenuOpen(false);
  };
  
  const handleMakePayment = () => {
    navigate("/makepayment");
    setMobileMenuOpen(false);
  };

   const login = useLogin();
  
  const handleLoginClick = () => {
    // Use SDK's useLogin hook to handle Keycloak login with PKCE
    // SDK automatically generates and stores PKCE parameters
    const redirectUri = `${window.location.origin}/callback`;
    login({ redirectUri });
  };
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };


  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
      {/* Header */}
      <AppBar
        position="static"
        style={{ backgroundColor: "#002664", color: "white" }}
        elevation={1}
      >
        <Toolbar>
          <Box display="flex" flexGrow={1} alignItems="center">
            <Box
              sx={{
                bgcolor: "primary.dark",
                color: "white",
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontWeight: "bold",
              }}
            >
              DSV |
            </Box>
            <Typography variant="body2" color="white" sx={{ ml: 1 }}>
              <b> eFairs </b>
            </Typography>
          </Box>
          <Box display={{ xs: "none", md: "flex" }} gap={3} alignItems="center">
            <MuiLink
              onClick={handleTrackshipments}
              color="white"
              underline="hover"
              sx={{ cursor: "pointer" }}
            >
              <b>Track Shipment</b>
            </MuiLink>
            <MuiLink
              onClick={handleMakePayment}
              color="white"
              underline="hover"
              sx={{ cursor: "pointer" }}
            >
              <b> Make Payment</b>
            </MuiLink>
            <MuiLink
              //href={loginUrl}
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              color="white"
              underline="hover"
              onClick={handleLoginClick}
            >
              <b>Login</b>
            </MuiLink>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              color="primary"
            >
              <b> Sign Up </b>
            </Button>
          </Box>
          <Box display={{ xs: "flex", md: "none" }}>
            <IconButton 
              color="inherit" 
              onClick={toggleMobileMenu}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: 280 },
        }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">
            Menu
          </Typography>
          <IconButton onClick={toggleMobileMenu} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleTrackshipments}>
              <ListItemText primary="Track Shipment" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleMakePayment}>
              <ListItemText primary="Make Payment" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
           <ListItemButton onClick={handleLoginClick}>
              <ListItemText primary="Login" />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 1 }} />
          <ListItem disablePadding>
            <ListItemButton 
              component={Link} 
              to="/signup"
              onClick={() => setMobileMenuOpen(false)}
            >
              <ListItemText 
                primary="Sign Up" 
                primaryTypographyProps={{ fontWeight: "bold", color: "primary.main" }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          height: 600,
          backgroundColor: "grey.900",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={img}
          alt="Event background"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.7,
            position: "absolute",
          }}
        />
        <Container
          sx={{
            position: "relative",
            zIndex: 10,
            height: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            maxWidth={600}
            bgcolor="primary.dark"
            color="white"
            p={4}
            borderRadius={2}
          >
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Event and exhibition logistics solutions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box py={8} bgcolor="grey.50">
        <Container>
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Our Services
            </Typography>
            <Box
              textAlign="left"
              color="text.secondary"
              maxWidth={800}
              mx="auto"
            >
              <Typography variant="h6" paragraph>
                Are you looking for a reliable event and exhibition logistics
                company? Look no further than DSV. We offer a comprehensive
                range of services tailored to meet your specific needs for
                transporting trade show freight and event equipment. Our team of
                logistics experts has extensive experience in handling all types
                of exhibition freight services and event transportation, from
                small local exhibitions to large international events.
              </Typography>
              <Typography variant="h6" paragraph>
                We understand the unique challenges of delivering time-critical
                goods in the face of huge crowds, open day pressure, and complex
                logistical requirements. At DSV, we work closely with you to
                ensure seamless, reliable transportation of your event
                materials, including trade show booths, exhibits, and other
                equipment.
              </Typography>
              <Typography variant="h6" paragraph>
                Our industry-leading transportation network and cutting-edge
                technology ensure timely and secure delivery, no matter where
                your event is located. We also offer a range of value-added
                services to support your event logistics needs, including
                customs clearance, storage, and on-site support. With DSV as
                your partner, you can rest assured that your event logistics are
                in safe hands.
              </Typography>
              <Typography variant="h6" paragraph>
                Contact us today to learn more about our exhibition logistics
                and event transport solutions and how we can help make your next
                event a success.
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(6, minmax(0, 1fr))",
              },
            }}
          >
            {SERVICES.map(([title, desc], idx) => (
              <Box key={idx}>
                <Card
                  elevation={1}
                  sx={{ 
                    height: "100%",
                    transition: "0.3s", 
                    "&:hover": { boxShadow: 6 } 
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: "blue.100",
                        borderRadius: 2,
                        mb: 2,
                      }}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: "blue.600",
                          borderRadius: "50%",
                        }}
                      />
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {title}
                    </Typography>
                    <Typography color="text.secondary">{desc}</Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={8} bgcolor="primary.dark" color="white" textAlign="center">
        <Container>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography
            variant="h6"
            color="blue.100"
            maxWidth={600}
            mx="auto"
            gutterBottom
          >
            Join thousands of satisfied customers who trust us with their event
            management needs
          </Typography>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            justifyContent="center"
            mt={3}
          >
           
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box bgcolor="grey.900" color="white" py={6}>
        <Container>
          <Grid container spacing={4}>
            <Grid sx={{ xs: 12, md: 3 }} >
              <Box mb={2}>
                <Box
                  bgcolor="primary.main"
                  px={2}
                  py={0.5}
                  borderRadius={1}
                  fontWeight="bold"
                  display="inline-block"
                >
                  DSV
                </Box>
              </Box>
              <Typography color="grey.400">
                Leading provider of event management and logistics solutions
                worldwide.
              </Typography>
            </Grid>
            {FOOTER_SECTIONS.map(([section, items], index) => (
              <Grid sx={{xs:12, sm:4, md:3}}  key={index}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                  {section}
                </Typography>
                {items.map((item) => (
                  <Typography key={item} color="grey.400" component="div" mb={1}>
                    <MuiLink href="#" color="inherit" underline="hover">
                      {item}
                    </MuiLink>
                  </Typography>
                ))}
              </Grid>
            ))}
          </Grid>
          <Box
            mt={6}
            pt={3}
            borderTop={1}
            borderColor="grey.800"
            textAlign="center"
          >
            <Typography variant="body2" color="grey.400">
              &copy; {new Date().getFullYear()} DSV eFairs. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
